import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { query } from "@/services/services";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { Panel } from "@/components/common/Panel";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { formatTime, formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Printer, ListChecks, Sparkles, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PREP_STYLE = { READY: "bg-success-bg text-success", PARTIAL: "bg-warning-bg text-warning", GENERATING: "bg-copper-light/40 text-copper", FAILED: "bg-danger/10 text-danger" };

const RANGE = [
  { key: "today", label: "Today", off: 0 },
  { key: "tomorrow", label: "Tomorrow", off: 1 },
];

export default function CauseListBoard() {
  const { db, persona, can } = useApp();
  const navigate = useNavigate();
  const [range, setRange] = useState("today");
  const [preps, setPreps] = useState({});
  const [prepping, setPrepping] = useState(false);

  const targetDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + (RANGE.find((r) => r.key === range)?.off || 0));
    return d;
  }, [range]);
  const target = targetDate.toDateString();
  const iso = `${targetDate.getFullYear()}-${String(targetDate.getMonth() + 1).padStart(2, "0")}-${String(targetDate.getDate()).padStart(2, "0")}`;

  const loadPreps = () => axios.get(`${API}/cause-list/prep-status`, { params: { date: iso } }).then((r) => setPreps(Object.fromEntries(r.data.items.map((i) => [i.hearingId, i.prep])))).catch(() => {});
  useEffect(() => { loadPreps(); }, [iso]); // eslint-disable-line
  const generating = Object.values(preps).some((p) => p?.status === "GENERATING");
  useEffect(() => {
    if (!generating) return;
    const t = setInterval(loadPreps, 5000);
    return () => clearInterval(t);
  }, [generating, iso]); // eslint-disable-line

  const prepAll = async () => {
    setPrepping(true);
    try {
      const r = await axios.post(`${API}/cause-list/prep-all`, { date: iso });
      toast.success(`${r.data.queued.length} hearing card${r.data.queued.length === 1 ? "" : "s"} queued${r.data.skipped.length ? ` · ${r.data.skipped.length} already prepared` : ""}`);
      await loadPreps();
    } catch (e) { const d = e?.response?.data?.detail; toast.error(typeof d === "string" ? d : "Could not queue hearing prep"); }
    finally { setPrepping(false); }
  };

  // Group by court → bench/judge
  const grouped = useMemo(() => {
    const listed = db.hearings
      .filter((h) => !h.outcome && new Date(h.date).toDateString() === target)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    const byCourt = {};
    listed.forEach((h) => {
      const court = h.court || "Unassigned Court";
      const bench = h.judge || "Bench to be notified";
      byCourt[court] = byCourt[court] || {};
      byCourt[court][bench] = byCourt[court][bench] || [];
      byCourt[court][bench].push(h);
    });
    return byCourt;
  }, [db.hearings, target]);

  const courts = Object.keys(grouped);
  const total = courts.reduce((s, c) => s + Object.values(grouped[c]).reduce((a, b) => a + b.length, 0), 0);

  return (
    <div>
      <style>{`@media print{aside,header,.no-print{display:none!important}main{overflow:visible!important}.print-board{box-shadow:none!important;border:1px solid #ccc}}`}</style>
      <PageHeader title="Cause List Board" subtitle="The day's listings grouped by court and bench — ready to print for chamber use.">
        <div className="no-print flex items-center gap-2">
          <div className="flex gap-1">{RANGE.map((r) => (
            <button key={r.key} onClick={() => setRange(r.key)} data-testid={`causelist-range-${r.key}`}
              className={cn("rounded-md px-3 py-1 text-xs font-medium", range === r.key ? "bg-navy text-white" : "border border-line text-ink-2")}>{r.label}</button>
          ))}</div>
          {can("research.view") && total > 0 && (
            <Button size="sm" onClick={prepAll} disabled={prepping || generating} data-testid="prep-all-btn">
              {prepping || generating ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1.5 h-3.5 w-3.5" />}
              {generating ? "Preparing cards…" : "Prep all"}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="print-causelist-btn"><Printer className="mr-1.5 h-3.5 w-3.5" /> Print Board</Button>
        </div>
      </PageHeader>

      {/* Board header (visible in print) */}
      <div className="print-board mb-4 rounded-xl border border-line bg-card p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-display text-2xl font-semibold text-ink">Daily Cause List</div>
            <div className="text-sm text-ink-2">{formatDate(target, "EEEE, dd MMMM yyyy")} · {persona.org}</div>
          </div>
          <div className="text-right text-sm text-ink-2">{total} matter{total === 1 ? "" : "s"} listed · {courts.length} court{courts.length === 1 ? "" : "s"}</div>
        </div>
      </div>

      {total === 0 ? (
        <EmptyState icon={ListChecks} title="No matters listed" message={`Nothing is listed for ${range}. Enjoy the chamber time.`} />
      ) : (
        <div className="space-y-5">
          {courts.map((court) => (
            <Panel key={court} className="print-board overflow-hidden">
              <div className="border-b border-line bg-navy px-4 py-2.5">
                <div className="font-display text-lg font-semibold text-white">{court}</div>
              </div>
              {Object.entries(grouped[court]).map(([bench, items]) => (
                <div key={bench} className="border-b border-line last:border-0">
                  <div className="bg-surface px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-copper">{bench}</div>
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-line text-left text-[11px] uppercase text-ink-muted">
                      <th className="w-12 px-4 py-1.5 font-medium">Sl.</th><th className="px-3 py-1.5 font-medium">Time</th><th className="px-3 py-1.5 font-medium">Case No.</th><th className="px-3 py-1.5 font-medium">Matter</th><th className="px-3 py-1.5 font-medium">Purpose</th><th className="px-3 py-1.5 font-medium">Counsel</th><th className="no-print px-3 py-1.5 font-medium">Prep card</th>
                    </tr></thead>
                    <tbody>{items.map((h, i) => {
                      const m = query.matter(db, h.matterId);
                      return (
                        <tr key={h.id} className="cursor-pointer border-b border-line last:border-0 hover:bg-surface" onClick={() => navigate(`/matters/${h.matterId}`)} data-testid={`board-item-${h.id}`}>
                          <td className="px-4 py-2 font-medium text-ink-2">{i + 1}</td>
                          <td className="px-3 py-2 text-copper">{formatTime(h.date)}</td>
                          <td className="px-3 py-2 text-ink-2 whitespace-nowrap">{m?.caseNumber}</td>
                          <td className="px-3 py-2 font-medium text-ink">{m?.title}</td>
                          <td className="px-3 py-2 text-ink-2">{h.purpose}</td>
                          <td className="px-3 py-2 text-ink-2">{m ? query.userName(db, m.leadId) : "—"}</td>
                          <td className="no-print px-3 py-2" data-testid={`prep-status-${h.id}`}>{preps[h.id] ? <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium", PREP_STYLE[preps[h.id].status])}>{preps[h.id].status === "GENERATING" && <Loader2 className="h-2.5 w-2.5 animate-spin" />}{preps[h.id].status}</span> : <span className="text-[11px] text-ink-muted">Not prepared</span>}</td>
                        </tr>
                      );
                    })}</tbody>
                  </table>
                </div>
              ))}
            </Panel>
          ))}
        </div>
      )}
    </div>
  );
}
